import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { API_BASE, getShareInfo } from '../api/client';

/**
 * The share page a recipient opens (card t_31859fc7 §1).
 *
 * Deliberately OUTSIDE the app shell and outside auth: a share link's whole point is that the
 * recipient has no account. The token in the URL is the credential — every request this page
 * makes is one of the public `/share/:token*` routes, which is why it renders for a stranger in an
 * incognito window.
 *
 * The player streams straight from `GET /share/:token` (byte-range aware, counted on every
 * request) rather than from a public bucket URL: safeguarding §3.3.3 bans unfettered R2 URLs.
 */
interface ShareInfo {
  token: string;
  kind: 'clip' | 'reel';
  title: string;
  clipCount: number;
  durationSeconds: number | null;
  sizeBytes: number | null;
  teamName: string;
  season: string | null;
  createdAt: string;
  expiresAt: string | null;
  accessCount: number;
  lastAccessAt: string | null;
  revoked: boolean;
}

export function ShareRoute() {
  const { token = '' } = useParams<{ token: string }>();
  const [info, setInfo] = useState<ShareInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setInfo(null);
    setError(null);
    if (!token) {
      setError('This share link is not available.');
      return;
    }
    void getShareInfo(token)
      .then((payload) => {
        if (!cancelled) setInfo(payload.share);
      })
      .catch(() => {
        if (!cancelled) setError('This share link is not available. It may have expired, been revoked, or never existed.');
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const streamUrl = `${API_BASE}/share/${encodeURIComponent(token)}`;

  return (
    <div className="min-h-full bg-charcoal text-chalk">
      <header className="flex items-center gap-3 border-b border-chalk/10 px-5 py-4">
        <span className="font-semibold tracking-tight">
          <span className="text-copper">Callout</span>
        </span>
        {info && <span className="text-sm text-chalk-200">{info.teamName}</span>}
      </header>

      <main className="mx-auto w-full max-w-3xl px-5 py-6" data-testid="share-page" data-share-token={token}>
        {error && (
          <div className="py-10" data-testid="share-error">
            <h1 className="text-lg font-semibold">This share link is not available</h1>
            <p className="mt-2 text-sm text-chalk-200">{error}</p>
          </div>
        )}

        {!error && !info && (
          <p className="py-10 text-sm text-chalk-200" data-testid="share-loading">
            Loading shared clip…
          </p>
        )}

        {info && (
          <>
            <h1 className="text-xl font-semibold" data-testid="share-title">
              {info.title}
            </h1>
            <p className="mt-1 text-sm text-chalk-200" data-testid="share-meta">
              <span className="rounded-full bg-copper px-2.5 py-0.5 text-xs font-semibold text-charcoal">
                {info.kind}
              </span>
              {info.durationSeconds ? ` · ${info.durationSeconds.toFixed(1)}s` : ''}
              {info.clipCount ? ` · ${info.clipCount} clip${info.clipCount === 1 ? '' : 's'}` : ''}
              {info.season ? ` · ${info.season}` : ''}
            </p>

            <video
              data-testid="share-player"
              className="mt-4 w-full rounded-lg bg-black"
              controls
              playsInline
              preload="metadata"
              src={streamUrl}
              onPlay={() => setPlaying(true)}
            />

            <div className="mt-4 rounded-lg border border-chalk/10 p-4 text-sm">
              <p className="text-chalk-200">
                <strong className="text-chalk">{info.teamName}</strong> shared this with Callout, the
                voice-first tagging tool for rugby league coaches.
              </p>
              <p className="mt-2 text-chalk-200" data-testid="share-expiry">
                {info.expiresAt
                  ? `This link expires ${new Date(info.expiresAt).toUTCString()}.`
                  : 'This link does not expire.'}
              </p>
              <p className="mt-2 text-chalk-200">
                Views so far:{' '}
                <span data-testid="share-access-count">{info.accessCount}</span>
                {playing ? ' (including this one)' : ''}
              </p>
              <a
                className="mt-3 inline-block rounded bg-copper px-3 py-1.5 text-sm font-semibold text-charcoal"
                href={streamUrl}
                download
                data-testid="share-download"
              >
                Download MP4
              </a>
            </div>
          </>
        )}
      </main>

      <footer className="mx-auto w-full max-w-3xl px-5 pb-10 text-xs text-chalk-200">
        Only people with this link can see it — there is no public gallery. <Link to="/privacy" className="text-copper underline">Privacy</Link>.
      </footer>
    </div>
  );
}
